import {
  Alert,
  Button,
  Chip,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  decideName,
  downloadBlob,
  nameRequests,
  submitName,
  workflowFile,
  updateListingDate,
} from "../service/WorkflowService";
import { getCompanyProfile } from "features/account/service/AccountProfileService";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

const dateOnly = (value) => {
  if (!value) return "—";

  const match = String(value).match(
    /^(\d{4}-\d{2}-\d{2})/,
  );

  return match?.[1] || "—";
};

const dateTime = (value) => {
  if (!value) return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleString();
};

const statusPresentation = {
  SUBMITTED: {
    label: "Menunggu Verifikasi",
    color: "info",
  },
  APPROVED: {
    label: "Disetujui",
    color: "success",
  },
  REJECTED: {
    label: "Ditolak",
    color: "error",
  },
};

export default function NameChangePanel({
  companyId,
  companyName,
  canReview = false,
}) {
  const { t } = useCompanyDocumentsLanguage();

  const requestText = (key, fallback, params) =>
    t(key, params) || fallback || "";

  const [rows, setRows] = useState([]);

  const [profile, setProfile] = useState({
    name: companyName || "",
    name_at_listing: null,
    effect_code: "",
    listing_date: null,
  });

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [effectiveDate, setEffectiveDate] =
    useState("");
  const [file, setFile] = useState(null);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const [profileEditing, setProfileEditing] =
    useState(false);
  const [listingName, setListingName] =
    useState("");
  const [listingDate, setListingDate] =
    useState("");
  const [profileBusy, setProfileBusy] =
    useState(false);
  const [profileError, setProfileError] =
    useState("");
  const [profileSuccess, setProfileSuccess] =
    useState("");

const load = async () => {
    try {
      const data = await nameRequests(companyId);
      setRows(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    let active = true;

    setRows([]);
    setError("");
    setNote("");
    setShowForm(false);
    setName("");
    setEffectiveDate("");
    setFile(null);
    setProfileEditing(false);
    setListingName("");
    setListingDate("");
    setProfileError("");
    setProfileSuccess("");

    setProfile({
      name: companyName || "",
      name_at_listing: null,
      effect_code: "",
      listing_date: null,
    });

    nameRequests(companyId)
      .then((data) => {
        if (active) {
          setRows(data);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err.message);
        }
      });

    if (!companyId) {
      getCompanyProfile()
        .then((response) => {
          if (!active) return;

          const data = response?.data || {};

          setProfile({
            name:
              data.name ||
              data.company_name ||
              "",
            name_at_listing:
              data.name_at_listing ?? null,
            effect_code:
              data.effect_code || "",
            listing_date:
              data.listing_date ?? null,
          });
        })
        .catch(() => {});
    }

    return () => {
      active = false;
    };
  }, [companyId, companyName]); // eslint-disable-line react-hooks/exhaustive-deps


  async function act(fn) {
    setBusy(true);
    setError("");

    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function startProfileEdit() {
    setListingName(
      profile.name_at_listing || "",
    );

    setListingDate(
      dateOnly(profile.listing_date) === "—"
        ? ""
        : dateOnly(profile.listing_date),
    );

    setProfileError("");
    setProfileSuccess("");
    setProfileEditing(true);
  }

  function cancelProfileEdit() {
    setProfileEditing(false);
    setProfileError("");
  }

  async function saveProfileListingMetadata() {
    if (!listingDate) {
      setProfileError(
        t("client.profile.listingDateRequired"),
      );

      return;
    }

    setProfileBusy(true);
    setProfileError("");
    setProfileSuccess("");

    try {
      const cleanListingName =
        listingName.trim();

      const result = await updateListingDate(
        listingDate,
        cleanListingName,
      );

      setProfile((current) => ({
        ...current,
        name_at_listing:
          Object.prototype.hasOwnProperty.call(
            result || {},
            "name_at_listing",
          )
            ? result.name_at_listing
            : cleanListingName || null,
        listing_date:
          result?.listing_date ||
          listingDate,
      }));

      setProfileEditing(false);
      setProfileSuccess(
        t("client.profile.listingUpdated"),
      );
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileBusy(false);
    }
  }

  const currentName =
    profile.name ||
    companyName ||
    "—";

  const pending = rows.find(
    (row) => row.status === "SUBMITTED",
  );

  const historyRows = rows.filter(
    (row) => row !== pending,
  );

  async function handleSubmit() {
    if (
      !name.trim() ||
      !effectiveDate ||
      !file
    ) {
      return;
    }

    await act(async () => {
      await submitName(
        name.trim(),
        effectiveDate,
        file,
      );

      setName("");
      setEffectiveDate("");
      setFile(null);
      setShowForm(false);
    });
  }

  async function previewFile(row) {
    const preview =
      window.open(
        "",
        "_blank",
      );

    if (preview) {
      preview.opener = null;
    }

    if (!preview) {
      throw new Error(
        requestText("client.profile.requestCard.popupBlocked"),
      );
    }

    try {
      const blob =
        await workflowFile(
          row.id,
          !!companyId,
          true,
        );

      if (
        blob.type !==
          "application/pdf" ||
        (await blob
          .slice(0, 5)
          .text()) !== "%PDF-"
      ) {
        throw new Error(
          requestText("client.profile.requestCard.previewUnavailable"),
        );
      }

      const objectURL =
        URL.createObjectURL(
          blob,
        );

      preview.location.href =
        objectURL;

      window.setTimeout(
        () => {
          URL.revokeObjectURL(
            objectURL,
          );
        },
        60000,
      );
    } catch (err) {
      preview.close();
      throw err;
    }
  }

  async function downloadFile(row) {
    const blob = await workflowFile(
      row.id,
      !!companyId,
    );

    downloadBlob(
      blob,
      companyId
        ? row.stored_filename ||
            "company-name-change.pdf"
        : row.original_filename ||
            "company-name-change.pdf",
    );
  }

  function renderRequest(row) {
    const basePresentation =
      statusPresentation[row.status] || {
        label: row.status || "—",
        color: "default",
      };

    const statusKey = {
      SUBMITTED: "submitted",
      APPROVED: "approved",
      REJECTED: "rejected",
    }[row.status];

    const presentation = {
      ...basePresentation,
      label: statusKey
        ? requestText(
            `client.profile.status.${statusKey}`,
            basePresentation.label,
          )
        : basePresentation.label,
    };

    return (
      <article
        key={row.id}
        className="rounded-lg border p-4 grid gap-4"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-[#5F737F] mb-1">
              {requestText(
                "client.profile.requestCard.title",
                "Perubahan Nama Perseroan",
              )}
            </p>

            <p className="font-semibold break-words">
              {row.old_name || "—"}

              <span className="mx-2 text-[#83919B]">
                →
              </span>

              {row.proposed_name || "—"}
            </p>
          </div>

          <Chip
            size="small"
            variant="outlined"
            color={presentation.color}
            label={presentation.label}
          />
        </div>

        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-[#5F737F]">
              {requestText(
                "client.profile.requestCard.effectiveDate",
                "Tanggal Efektif",
              )}
            </p>

            <p className="mt-1">
              {dateOnly(
                row.effective_date,
              )}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#5F737F]">
              {requestText(
                "client.profile.requestCard.submitted",
                "Diajukan",
              )}
            </p>

            <p className="mt-1">
              {dateTime(
                row.submitted_at,
              )}
            </p>
          </div>

          <div className="min-w-0">
            <p className="text-xs text-[#5F737F]">
              {requestText(
                "client.profile.requestCard.supportingDocument",
                "Dokumen Pendukung",
              )}
            </p>

            <p className="mt-1 break-all">
              {companyId
                ? row.stored_filename ||
                  "—"
                : row.original_filename ||
                  "—"}
            </p>
          </div>
        </div>

        {row.requester_name &&
          companyId && (
            <p className="text-sm text-[#5F737F]">
              {requestText(
                "client.profile.requestCard.submittedBy",
                "Diajukan oleh",
              )}{" "}
              {row.requester_name}
            </p>
          )}

        {row.review_note && (
          <div className="rounded-lg bg-[#F7F9FC] p-3">
            <p className="text-xs font-semibold text-[#5F737F]">
              {requestText(
                "client.profile.requestCard.note",
                "Catatan SIGU",
              )}
            </p>

            <p className="text-sm mt-1 whitespace-pre-wrap">
              {row.review_note}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() =>
              act(() =>
                previewFile(row),
              )
            }
          >
            {requestText("client.profile.requestCard.preview")}
          </Button>

          <Button
            size="small"
            disabled={busy}
            onClick={() =>
              act(() =>
                downloadFile(row),
              )
            }
          >
            {requestText("client.profile.requestCard.download")}
          </Button>
        </div>

        {companyId &&
          canReview &&
          row.status ===
            "SUBMITTED" && (
            <div className="border-t pt-4 grid gap-3">
              <TextField
                label={t("adminReview.profile.reviewNote")}
                multiline
                minRows={2}
                value={note}
                onChange={(event) =>
                  setNote(
                    event.target.value,
                  )
                }
                inputProps={{
                  maxLength: 2000,
                }}
              />

              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  disabled={
                    busy ||
                    !note.trim()
                  }
                  color="error"
                  variant="outlined"
                  onClick={() =>
                    act(() =>
                      decideName(
                        row.id,
                        "REJECTED",
                        note,
                      ),
                    )
                  }
                >{t("adminReview.profile.reject")}</Button>

                <Button
                  disabled={busy}
                  variant="contained"
                  onClick={() =>
                    act(() =>
                      decideName(
                        row.id,
                        "APPROVED",
                        note,
                      ),
                    )
                  }
                >{t("adminReview.profile.approve")}</Button>
              </div>
            </div>
          )}
      </article>
    );
  }

  /*
   * Admin keeps the review-oriented presentation.
   * The redesign below is focused on the client Company Profile UI.
   */
  if (companyId) {
    return (
      <section className="border rounded-lg p-4 grid gap-4">
        <div>
          <h3 className="font-semibold">
            {t("adminReview.overview.nameChanges")}
          </h3>

          <p className="text-sm text-[#5F737F] mt-1">
            {t("adminReview.profile.description")}
          </p>
        </div>

        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        {!rows.length ? (
          <p className="text-sm text-[#5F737F]">
            {t("adminReview.overview.noNameChanges")}
          </p>
        ) : (
          <div className="grid gap-3">
            {rows.map(renderRequest)}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="bg-white shadow-sm rounded-lg p-4 md:p-6 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-lg font-bold">
            {t("client.profile.title")}
          </h2>

          <p className="text-sm text-[#5F737F] mt-1">
            {t("client.profile.description")}
          </p>
        </div>

        {!pending &&
          !showForm && (
            <Button
              variant="outlined"
              onClick={() =>
                setShowForm(true)
              }
            >
              {t("client.profile.requestChange")}
            </Button>
          )}
      </div>

      {error && (
        <Alert
          severity="error"
          className="mb-4"
        >
          {error}
        </Alert>
      )}

      <div className="rounded-lg border p-4">
        <div className="flex items-start justify-between gap-4 pb-4 border-b">
          <div className="min-w-0">
            <p className="text-xs text-[#5F737F] mb-1">
              {t("client.profile.currentCompanyName")}
            </p>

            <p className="text-lg font-semibold break-words">
              {currentName}
            </p>
          </div>

          {!companyId &&
            (!profileEditing ? (
              <Button
                variant="text"
                size="small"
                aria-label={t("client.profile.editListing")}
                onClick={startProfileEdit}
              >
                  {t("client.profile.editListing")}
                </Button>
            ) : (
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="contained"
                  size="small"
                  aria-label={t("client.profile.saveListingAria")}
                  disabled={
                    profileBusy ||
                    !listingDate
                  }
                  onClick={
                    saveProfileListingMetadata
                  }
                >
                  {profileBusy
                    ? t("client.profile.saving")
                    : t("client.profile.save")}
                </Button>

                <Button
                  size="small"
                  aria-label={t("client.profile.cancelListingAria")}
                  disabled={profileBusy}
                  onClick={cancelProfileEdit}
                >
                  {t("client.profile.cancel")}
                </Button>
              </div>
            ))}
        </div>

        <div className="grid md:grid-cols-3 gap-5 pt-4">
          <div>
            <p className="text-xs text-[#5F737F]">
              {t("client.profile.issuerCode")}
            </p>

            <p className="text-sm font-medium mt-1">
              {profile.effect_code || "—"}
            </p>
          </div>

          <div>
            {!profileEditing ? (
              <>
                <p className="text-xs text-[#5F737F]">
                  {t("client.profile.listingName")}
                </p>

                <p className="text-sm font-medium mt-1 break-words">
                  {profile.name_at_listing || "—"}
                </p>
              </>
            ) : (
              <TextField
                fullWidth
                label={t("client.profile.listingName")}
                size="small"
                value={listingName}
                onChange={(event) =>
                  setListingName(
                    event.target.value,
                  )
                }
                inputProps={{
                  maxLength: 100,
                }}
              />
            )}
          </div>

          <div>
            {!profileEditing ? (
              <>
                <p className="text-xs text-[#5F737F]">
                  {t("client.profile.listingDate")}
                </p>

                <p className="text-sm font-medium mt-1">
                  {dateOnly(
                    profile.listing_date,
                  )}
                </p>
              </>
            ) : (
              <TextField
                fullWidth
                label={t("client.profile.listingDate")}
                type="date"
                size="small"
                value={listingDate}
                onChange={(event) =>
                  setListingDate(
                    event.target.value,
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          </div>
        </div>

        {profileError && (
          <Alert
            severity="error"
            className="mt-4"
          >
            {profileError}
          </Alert>
        )}

        {profileSuccess && (
          <Alert
            severity="success"
            className="mt-4"
          >
            {profileSuccess}
          </Alert>
        )}
      </div>

      {pending && (
        <div className="mt-5 grid gap-3">
          <Alert severity="info">
            {t("client.profile.pending")}
          </Alert>

          {renderRequest(pending)}
        </div>
      )}

      {!pending &&
        showForm && (
          <div className="rounded-lg border p-4 md:p-5 mt-5 grid gap-5">
            <div>
              <h3 className="font-semibold">
                {t("client.profile.requestChange")}
              </h3>

              <p className="text-sm text-[#5F737F] mt-1">
                {t("client.profile.currentName")}{" "}
                <span className="font-medium text-[#263238]">
                  {currentName}
                </span>
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <TextField
                label={t("client.profile.newName")}
                value={name}
                onChange={(event) =>
                  setName(
                    event.target.value,
                  )
                }
                inputProps={{
                  maxLength: 100,
                }}
                helperText={t("client.profile.characters", {
                  count: name.length,
                })}
                fullWidth
              />

              <TextField
                label={t("client.profile.effectiveChangeDate")}
                type="date"
                value={effectiveDate}
                onChange={(event) =>
                  setEffectiveDate(
                    event.target.value,
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </div>

            <div className="rounded-lg border border-dashed bg-[#FAFBFC] p-4">
              <p className="font-semibold text-sm">
                {t("client.profile.supportingDocument")}
              </p>

              <p className="text-sm text-[#5F737F] mt-1 mb-3 max-w-3xl">
                {t("client.profile.supportingDescription")}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                >
                  {t("client.profile.choosePdf")}

                  <input
                    hidden
                    aria-label={t("client.profile.supportingDocument")}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) =>
                      setFile(
                        event.target
                          .files?.[0] ||
                          null,
                      )
                    }
                  />
                </Button>

                <span className="text-sm text-[#5F737F] break-all">
                  {file
                    ? file.name
                    : t("client.profile.noFile")}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                disabled={busy}
                onClick={() => {
                  setShowForm(false);
                  setName("");
                  setEffectiveDate("");
                  setFile(null);
                }}
              >
                {t("client.profile.cancel")}
              </Button>

              <Button
                variant="contained"
                disabled={
                  busy ||
                  !name.trim() ||
                  !effectiveDate ||
                  !file
                }
                onClick={
                  handleSubmit
                }
              >
                {t("client.profile.requestChange")}
              </Button>
            </div>
          </div>
        )}

      {!!historyRows.length && (
        <div className="mt-6">
          <div className="mb-3">
            <h3 className="font-semibold">
              {t("client.profile.historyTitle")}
            </h3>

            <p className="text-sm text-[#5F737F] mt-1">
              {t("client.profile.historyDescription")}
            </p>
          </div>

          <div className="grid gap-3">
            {historyRows.map(
              renderRequest,
            )}
          </div>
        </div>
      )}
    </section>
  );
}
